/**
 * MFAEnrollModal — Two-step TOTP enrollment dialog.
 *
 * Step 1: Display QR code + manual secret for the authenticator app.
 * Step 2: User enters 6-digit confirmation code to activate.
 */

import { useState, useEffect } from 'react';
import { Smartphone, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';

interface MFAEnrollModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function MFAEnrollModal({ open, onOpenChange, onSuccess }: MFAEnrollModalProps) {
  const { enrollMFA, verifyMFAEnrollment } = useAuth();

  const [step, setStep] = useState<'qr' | 'verify' | 'done'>('qr');
  const [factorId, setFactorId] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [enrollError, setEnrollError] = useState('');

  // Start enrollment when modal opens
  useEffect(() => {
    if (!open) return;
    setStep('qr');
    setCode('');
    setEnrollError('');

    enrollMFA().then(({ factorId: fid, qrCode: qr, secret: sec, error }) => {
      if (error) {
        toast.error('Failed to start 2FA setup: ' + error.message);
        onOpenChange(false);
        return;
      }
      setFactorId(fid);
      setQrCode(qr);
      setSecret(sec);
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setIsLoading(true);
    setEnrollError('');
    try {
      const { error } = await verifyMFAEnrollment(factorId, code);
      if (error) {
        setEnrollError(error.message || 'Invalid code. Check your authenticator app and try again.');
      } else {
        setStep('done');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    toast.success('Secret copied to clipboard');
  };

  const handleDone = () => {
    onSuccess();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {step === 'qr' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5 text-teal" />
                Set up Two-Factor Authentication
              </DialogTitle>
              <DialogDescription>
                Scan the QR code with an authenticator app like Google Authenticator, Authy, or 1Password.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* QR Code */}
              {qrCode ? (
                <div className="flex justify-center p-4 bg-card rounded-lg border">
                  <img src={qrCode} alt="TOTP QR Code" className="h-44 w-44" />
                </div>
              ) : (
                <div className="flex justify-center p-4 bg-muted rounded-lg h-52 items-center">
                  <p className="text-sm text-muted-foreground">Generating QR code...</p>
                </div>
              )}

              {/* Manual entry */}
              {secret && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Can't scan? Enter this code manually:
                  </Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded bg-muted px-3 py-2 text-xs font-mono tracking-widest break-all">
                      {secret}
                    </code>
                    <Button variant="outline" size="icon" className="shrink-0" onClick={handleCopySecret}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                className="bg-teal hover:bg-teal-hover text-white"
                onClick={() => setStep('verify')}
                disabled={!qrCode}
              >
                I've scanned it →
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'verify' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5 text-teal" />
                Confirm your authenticator
              </DialogTitle>
              <DialogDescription>
                Enter the 6-digit code from your authenticator app to complete setup.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleVerify} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="mfa-code">Verification code</Label>
                <Input
                  id="mfa-code"
                  type="text"
                  inputMode="numeric"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(val);
                    setEnrollError('');
                  }}
                  maxLength={6}
                  className="text-center text-lg tracking-[0.4em] font-mono"
                  autoFocus
                  autoComplete="one-time-code"
                />
                {enrollError && (
                  <p className="text-xs text-destructive">{enrollError}</p>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setStep('qr')}>
                  Back
                </Button>
                <Button
                  type="submit"
                  className="bg-teal hover:bg-teal-hover text-white"
                  disabled={code.length !== 6 || isLoading}
                >
                  {isLoading ? 'Verifying...' : 'Activate 2FA'}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}

        {step === 'done' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-teal" />
                Two-Factor Authentication enabled
              </DialogTitle>
              <DialogDescription>
                Your account is now protected with 2FA. You'll be asked for a code at each sign-in.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 flex justify-center">
              <div className="rounded-full bg-teal-light p-4">
                <CheckCircle2 className="h-10 w-10 text-teal" />
              </div>
            </div>

            <DialogFooter>
              <Button className="w-full bg-teal hover:bg-teal-hover text-white" onClick={handleDone}>
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
